import { fetchPage, postPage, deletePage } from "@/services/config";
import type {
  ContractResData,
  CustomerLoginResData,
  CustomerResData,
  InvoiceResData,
  ProductResData,
  RawContractData,
  RawCustomerData,
  RawLoginUser,
  RawPortalSettings,
  RawProductData
} from "@/types";

const serviceBase = "psa";


// Portal settings
export function getPortalSettings(tenantId: number): Promise<RawPortalSettings> {
  return fetchPage<RawPortalSettings>(serviceBase, `/${tenantId}/config/portal`);
}

export function editPortalSettings(
  tenantId: number,
  payload: Record<string, unknown>
): Promise<RawPortalSettings> {
  return postPage<RawPortalSettings>(serviceBase, `/${tenantId}/config/portal`, payload);
}


// Customers
export function getAllCustomers(
  tenantId: number,
  params: Record<string, unknown> = {}
): Promise<CustomerResData> {
  return fetchPage<CustomerResData>(serviceBase, `/${tenantId}/customer`, params);
}

export function getCustomer(tenantId: number, customerId: string): Promise<unknown> {
  return fetchPage(serviceBase, `/${tenantId}/customer/${customerId}`);
}

export function addCustomer(tenantId: number, payload: RawCustomerData): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/customer`, payload);
}

export function editCustomer(
  tenantId: number, customerId: string, payload: RawCustomerData
): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/customer/${customerId}`, payload, {}, "PATCH");
}

export function deleteCustomer(tenantId: number, customerId: string): Promise<unknown> {
  return deletePage(serviceBase, `/${tenantId}/customer/${customerId}`);
}


// Login users
export function getAllLoginUsers(
  tenantId: number,
  customerId: string,
  params: Record<string, unknown> = {}
): Promise<CustomerLoginResData> {
  return fetchPage<CustomerLoginResData>(
    serviceBase,
    `/${tenantId}/customer/${customerId}/login`,
    params
  );
}

export function addLoginUser(
  tenantId: number,
  customerId: string,
  payload: RawLoginUser
): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/customer/${customerId}/login`, payload);
}

export function editLoginUser(
  tenantId: number,
  customerId: string,
  loginId: string, // Currently using email as the login id
  payload: RawLoginUser
): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/customer/${customerId}/login/${loginId}`, payload, {}, "PATCH");
}

export function deleteLoginUser(
  tenantId: number,
  customerId: string,
  loginId: string
): Promise<unknown> {
  return deletePage(serviceBase, `/${tenantId}/customer/${customerId}/login/${loginId}`);
}


//  Invoices
export function getAllInvoices(
  tenantId: number, params: Record<string, unknown> = {}
): Promise<InvoiceResData> {
  return fetchPage<InvoiceResData>(serviceBase, `/${tenantId}/invoice`, params);
}


// Contracts
export function getAllContracts(
  tenantId: number,
  params: Record<string, unknown> = {}
): Promise<ContractResData> {
  return fetchPage<ContractResData>(serviceBase, `/${tenantId}/contract`, params);
}

export function getContract(tenantId: number, contractId: string): Promise<RawContractData> {
  return fetchPage<RawContractData>(serviceBase, `/${tenantId}/contract/${contractId}`);
}

export function addContract(tenantId: number, payload: RawContractData): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/contract`, payload);
}

export function editContract(
  tenantId: number, contractId: string, payload: Partial<RawContractData>
): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/contract/${contractId}`, payload, {}, "PATCH");
}

export function deleteContract(tenantId: number, contractId: string): Promise<unknown> {
  return deletePage(serviceBase, `/${tenantId}/contract/${contractId}`);
}


// Products
export function getAllProducts(
  tenantId: number,
  params: Record<string, unknown> = {}
): Promise<ProductResData> {
  return fetchPage<ProductResData>(serviceBase, `/${tenantId}/product`, params);
}

export function getProduct(tenantId: number, productId: string): Promise<RawProductData> {
  return fetchPage<RawProductData>(serviceBase, `/${tenantId}/product/${productId}`);
}

export function addProduct(tenantId: number, payload: RawProductData): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/product`, payload);
}

export function editProduct(
  tenantId: number, productId: string, payload: Partial<RawProductData>
): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/product/${productId}`, payload, {}, "PATCH");
}

export function deleteProduct(tenantId: number, productId: string): Promise<unknown> {
  return deletePage(serviceBase, `/${tenantId}/product/${productId}`);
}
